import {
	AdjustProjectPropertiesRequest,
	InitiateProjectRequest,
	Project,
	SharedData,
	StartProjectRequest,
} from '@/scripts/types/generated';
import {Page} from '@inertiajs/core';
import {sendUpdateRequest} from '@/scripts/utils/crud-requests.ts';
import {ref} from 'vue';

const projects = ref<Project[]>();
const activeProject = computed<Project>(() => usePage().props.activeProject);
const isLoading = ref<boolean>(false);

export const useProjects = () => {
	const refreshProjects = (page: Page<SharedData>) => {
		projects.value = page.props.projects;
	};

	const initProjects = async () => {
		while (isLoading.value) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		if (projects.value) return;

		isLoading.value = true;
		const page = await sendReloadRequest<SharedData>(['projects']);
		refreshProjects(page);
		isLoading.value = false;
	};

	const setProjects = (newProjects: Project[]) => {
		projects.value = newProjects;
	};

	const initiateProject = async (
		request: InitiateProjectRequest
	): Promise<Project> => {
		const page = await sendCreateRequest<SharedData>(
			route('tenant.projects.store'),
			request,
			['projects', 'activeProject']
		);

		return new Promise<Project>((resolve) => {
			refreshProjects(page);
			const newProject = projects.value.find(
				(project) => project.name === request.name
			);
			resolve(newProject);
		});
	};

	const launchProject = async (
		uuid: string,
		request: StartProjectRequest
	): Promise<Project> => {
		const page = await sendUpdateRequest<SharedData>(
			route('tenant.projects.start', {
				project: uuid,
			}),
			request,
			['projects']
		);

		return new Promise<Project>((resolve) => {
			refreshProjects(page);
			const newProject = projects.value.find(
				(project) => project.uuid === uuid
			);
			resolve(newProject);
		});
	};

	const adjustProjectProperties = async (
		uuid: string,
		request: AdjustProjectPropertiesRequest
	): Promise<Project> => {
		const page = await sendUpdateRequest<SharedData>(
			route('tenant.projects.update', {
				project: uuid,
			}),
			request,
			['projects']
		);

		return new Promise<Project>((resolve) => {
			refreshProjects(page);
			const updatedProject = projects.value.find(
				(project) => project.uuid === uuid
			);
			resolve(updatedProject);
		});
	};

	const archiveProject = async (uuid: string): Promise<void> => {
		const page = await sendDeleteRequest<SharedData>(
			route('tenant.projects.destroy', {
				project: uuid,
			}),
			['projects']
		);

		return new Promise<void>((resolve) => {
			refreshProjects(page);
			resolve();
		});
	};

	const setActiveProject = async (projectUuid: string | null) => {
		const page = await sendUpdateRequest<SharedData>(
			route('tenant.projects.activate', {
				project: projectUuid,
			}),
			{},
			[]
		);

		return new Promise<void>((resolve) => {
			refreshProjects(page);
			resolve();
		});
	};

	const clearActiveProject = async () => {
		const page = await sendUpdateRequest<SharedData>(
			route('tenant.projects.clear-active'),
			{},
			[]
		);

		return new Promise<void>((resolve) => {
			refreshProjects(page);
			resolve();
		});
	};

	return {
		projects,
		activeProject,
		initProjects,
		setProjects,
		createProject: initiateProject,
		updateProjectMetaInfo: adjustProjectProperties,
		startProject: launchProject,
		setActiveProject,
		clearActiveProject,
		deleteProject: archiveProject,
	};
};
